/**
 * TeamMemberPicker — modal that lets a team admin/dept-head add one or more
 * users to a team at a fixed level (MANAGER or EXECUTIVE).
 *
 * Visual language: warm-ivory via .aura-root primitives (.card, .chip, .btn).
 * Dark-mode safe via CSS tokens.
 *
 * Behaviour:
 *   - Search input (debounced 250ms) → GET /auth/users?limit=100
 *   - Department filter chip defaults to team's dept but can toggle to "Any".
 *   - Users already on the team are shown but disabled with an "Already on team" chip.
 *   - Footer button calls `onPick` once per selected user (parent fires POST
 *     /teams/:id/members for each — simple loop, no batch endpoint yet).
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Search, Users, Loader2, Check } from 'lucide-react';
import { api } from '@/utils/api';
import type { TeamMemberLevel, TeamMembership } from '@/api/teams';

interface Department {
  id: string;
  name: string;
  code?: string;
}

interface UserRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  designation?: string | null;
  /** Flat field — may be absent; use department.id as the source of truth (BUG-20). */
  departmentId?: string | null;
  department?: Department | null;
  isActive?: boolean;
}

interface TeamMemberPickerProps {
  open: boolean;
  onClose: () => void;
  teamDepartmentId: string;
  teamDepartmentName: string;
  level: Exclude<TeamMemberLevel, 'DEPARTMENT_HEAD'>;
  existingMemberships: TeamMembership[];
  onPick: (userIds: string[]) => Promise<void> | void;
}

export function TeamMemberPicker({
  open,
  onClose,
  teamDepartmentId,
  teamDepartmentName,
  level,
  existingMemberships,
  onPick,
}: TeamMemberPickerProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [scopeAll, setScopeAll] = useState(false); // false = team dept only
  const [submitting, setSubmitting] = useState(false);

  const existingIds = useMemo(
    () =>
      new Set(
        existingMemberships
          .filter((m) => !m.leftAt)
          .map((m) => m.userId),
      ),
    [existingMemberships],
  );

  // Reset when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setSelected(new Set());
      setScopeAll(false);
    }
  }, [open]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Load users (all active) — paginate/filter client-side for dept scope
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get('/auth/users', { params: { limit: 200 } });
        if (cancel) return;
        const list: UserRow[] = res.data?.data ?? res.data ?? [];
        setUsers(list);
      } catch {
        if (!cancel) setUsers([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = debounced.toLowerCase();
    return users.filter((u) => {
      if (u.isActive === false) return false;
      // BUG-20 fix: /auth/users returns department as a nested object { id, name }.
      // The flat `departmentId` field is NOT present in the API response, so we must
      // read u.department?.id instead. Falling back to u.departmentId keeps compat
      // if the API shape is ever updated to include the flat field.
      const userDeptId = u.department?.id ?? u.departmentId ?? null;
      if (!scopeAll && userDeptId !== teamDepartmentId) return false;
      if (!q) return true;
      const hay =
        `${u.firstName} ${u.lastName} ${u.email} ${u.designation ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, debounced, scopeAll, teamDepartmentId]);

  const toggle = (id: string) => {
    if (existingIds.has(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selected.size === 0) return;
    try {
      setSubmitting(true);
      await onPick(Array.from(selected));
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const levelLabel = level === 'MANAGER' ? 'Manager' : 'Executive';

  return (
    <div
      className="aura-root fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(27,23,19,0.45)' }}
      onClick={onClose}
    >
      <div
        className="mx-4 w-full max-w-[560px] max-h-[85vh] flex flex-col"
        style={{
          background: 'var(--ivory-1)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          boxShadow: 'var(--sh-3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid var(--line)' }}
        >
          <div>
            <h2
              style={{
                fontFamily: 'var(--ff-serif)',
                fontSize: 20,
                color: 'var(--ink-0)',
                margin: 0,
              }}
            >
              Add {levelLabel}
            </h2>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
              Pick users to add to this team
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* Search + scope */}
        <div
          className="px-5 py-3"
          style={{ borderBottom: '1px solid var(--line)' }}
        >
          <div
            className="relative"
            style={{
              background: 'var(--ivory-0)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-sm)',
            }}
          >
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--ink-3)',
              }}
            />
            <input
              type="text"
              placeholder="Search by name, email, designation…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full"
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                padding: '8px 10px 8px 30px',
                fontSize: 13,
                color: 'var(--ink-0)',
              }}
            />
          </div>
          <div className="row gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
            <button
              className="chip"
              onClick={() => setScopeAll(false)}
              style={{
                cursor: 'pointer',
                ...(scopeAll
                  ? {}
                  : {
                      background: 'var(--saffron-50)',
                      color: 'var(--saffron-600)',
                      borderColor: 'var(--saffron-100)',
                    }),
              }}
            >
              {teamDepartmentName} only
            </button>
            <button
              className="chip"
              onClick={() => setScopeAll(true)}
              style={{
                cursor: 'pointer',
                ...(scopeAll
                  ? {
                      background: 'var(--saffron-50)',
                      color: 'var(--saffron-600)',
                      borderColor: 'var(--saffron-100)',
                    }
                  : {}),
              }}
            >
              Any department
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 py-2" style={{ minHeight: 200 }}>
          {loading ? (
            <div
              className="row center"
              style={{
                justifyContent: 'center',
                padding: 32,
                color: 'var(--ink-3)',
                gap: 8,
              }}
            >
              <Loader2 size={16} className="animate-spin" />
              Loading users…
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="col center"
              style={{
                padding: 40,
                color: 'var(--ink-3)',
                alignItems: 'center',
                textAlign: 'center',
                gap: 8,
              }}
            >
              <Users size={28} />
              <div style={{ fontSize: 13 }}>No users match your filters.</div>
              {!scopeAll && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setScopeAll(true)}
                >
                  Search across all departments
                </button>
              )}
            </div>
          ) : (
            <div>
              {filtered.map((u) => {
                const already = existingIds.has(u.id);
                const isSelected = selected.has(u.id);
                // BUG-20 fix: same nested-object fix for the cross-dept badge.
                const userDeptId = u.department?.id ?? u.departmentId ?? null;
                const crossDept =
                  !!userDeptId && userDeptId !== teamDepartmentId;
                return (
                  <button
                    key={u.id}
                    disabled={already}
                    onClick={() => toggle(u.id)}
                    className="row center"
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 12px',
                      borderRadius: 'var(--r-sm)',
                      background: isSelected
                        ? 'var(--saffron-50)'
                        : 'transparent',
                      border: '1px solid transparent',
                      cursor: already ? 'not-allowed' : 'pointer',
                      opacity: already ? 0.55 : 1,
                      gap: 10,
                      marginBottom: 2,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected && !already)
                        (e.currentTarget as HTMLButtonElement).style.background =
                          'var(--ivory-2)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected && !already)
                        (e.currentTarget as HTMLButtonElement).style.background =
                          'transparent';
                    }}
                  >
                    <Avatar name={`${u.firstName} ${u.lastName}`} size={32} />
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: 'var(--ink-0)',
                        }}
                      >
                        {u.firstName} {u.lastName}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--ink-3)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {u.designation || u.email}
                        {u.department ? ` · ${u.department.name}` : ''}
                      </div>
                    </div>
                    {already ? (
                      <span className="chip">Already on team</span>
                    ) : crossDept ? (
                      <span className="chip chip-amber">Different dept</span>
                    ) : null}
                    {isSelected && (
                      <span
                        className="row center"
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: 'var(--saffron)',
                          color: 'white',
                          justifyContent: 'center',
                        }}
                      >
                        <Check size={12} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="row between center px-5 py-3"
          style={{ borderTop: '1px solid var(--line)' }}
        >
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {selected.size === 0
              ? 'Select users to add'
              : `${selected.size} selected`}
          </div>
          <div className="row gap-2">
            <button className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={selected.size === 0 || submitting}
              onClick={handleConfirm}
              style={{
                opacity: selected.size === 0 || submitting ? 0.5 : 1,
              }}
            >
              {submitting && <Loader2 size={12} className="animate-spin" />}
              Add {selected.size || ''} {levelLabel}
              {selected.size === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tiny avatar component ───────────────────────────────────────────────────
function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--ivory-2)',
        color: 'var(--ink-1)',
        border: '1px solid var(--line)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.38,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials || '?'}
    </div>
  );
}
